import { get, filter, set as sceneSet, setAll, find } from './scene';
import { openAnnotation as open, closeAnnotation as close } from './annotations';
import { animateCameraTo } from './player';
import { queueClip, getClips } from './animation';
import { getSceneId } from './sceneIO';

const CONFIG_QUERY = { type: 'Scene', plug: 'Player', property: 'configurator' };
//let configDetails = null;
let configDetails = {};

function set({ tags, value }) {
  return sceneSet(tags, value);
}

function show({ tags }) {
  return store => {
    return Promise.all(filter(store, tags).map((id) => {
      return store.dispatch(sceneSet({ id, plug: 'Properties', property: 'visible' }, true));
    }));
  }
}

function hide({ tags }) {
  return store => {
    return Promise.all(filter(store, tags).map((id) => {
      return store.dispatch(sceneSet({ id, plug: 'Properties', property: 'visible' }, false));
    }));
  }
}

function setDefaultColor({ tags, value }) {
  return store => {
    return Promise.all(filter(store, tags).map((id) => {
      return store.dispatch(sceneSet({ id, plug: 'Material', property: 'defaultColor' }, Number(value)));
    }));
  }
}


function moveCamera({ tags }) {
  return (store) => {
    const cameraId = find(store, tags);
    return cameraId ? store.dispatch(animateCameraTo(cameraId, 500)) : Promise.resolve(true);
  };
}

function assignMaterial({ tags, value }) {
  return (store) => {
    const materialId = get(store, { name: value, evalPlug: 'Material', property: 'id' });
    sceneSet({ name: tags, plug: 'Material', property: 'reference' }, materialId)(store);
  };
}

function playClip({ tags }) {
  return (store) => {
    const clips = getClips(store).filter((clip) => clip.name === tags);
    return Promise.all(clips.map((clip) => {
      return store.dispatch(queueClip(clip.id, { autoplay: true }));
    }));
  };
}

const availableActions = {
  show,
  hide,
  set,
  'assign-material': assignMaterial,
  'set-default-color': setDefaultColor,
  'move-camera': moveCamera,
  'play-clip': playClip,
};

export function executeVariant(optionName, variantName) {
  return (store) => {
    const option = getOptions(store)[optionName.trim()];
    if (!option) {
      throw new Error(`Option: ${optionName} does not exist`);
    }
    const variant = option[variantName.trim()];
    if (!variant) {
      throw new Error(`Variant: ${variantName} does not exist within option: ${optionName}`);
    }
    return Promise.all(variant.map(({ action, tags, value }) => {
      const actionFn = availableActions[action];
      return actionFn && tags !== '' && store.dispatch(actionFn({ tags, value }));
    }));
  };
}

/**
 * Add an option to the configurator.
 *
 * @private
 * @param {Option} option Object containing variant(s).
 * @returns {Promise}
 *
 */
export function setOptions(store, options) {
  const oldOptions = getOptions(store);
  Object.assign(oldOptions, options);
}

export function getOptions(store) {
  const sceneId = getSceneId(store);
  if (!configDetails[sceneId]) {
    let options = get(store, CONFIG_QUERY);
    try {
      options = JSON.parse(options);
    } catch (err) {
      options = {};
    }
    Object.keys(options).map((op) => {
      const variants = options[op];
      Object.keys(variants).map((variant) => {
        if (variants[variant] === null) {
          variants[variant] = [];
        }
      });
    });
    configDetails[sceneId] = options;
  }
  return configDetails[sceneId];
}

export function putOption(store, name, option = {}) {
  const config = getOptions(store);
  Object.assign(config[name], option);
}

/**
 * Add a variant to an option.
 *
 * @private
 * @param {Variant} variant An array of properties.
 * @returns {Promise}
 *
 */
export function putVariant(optionName, variantName, variant = []) {
  putOption(optionName, { [variantName]: variant });
}

export function removeVariant(optionName, variantName) {
  putOption(optionName, { [variantName]: undefined });
}

export function getVariant(store, optionName, variantName) {
  const options = getOptions(store);
  return (!options || !options[optionName]) ? [] : options[variantName];
}

const publicApi = {
  actions: { executeVariant },
  selectors: { getOptions, setOptions, putOption, putVariant, removeVariant, getVariant },
};

export default publicApi;



// WEBPACK FOOTER //
// ./src/modules/configuration.js